import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dynastyTokens } from '../../../services/colorService';
import PlayerLink from '../../../components/PlayerLink';
import { acceptColor, positionLabel } from './optimizerUtils';

const colors = dynastyTokens.colors;

/* ── Utilities ── */
const profileColor = (p) => {
  if (p === 'safe_bet') return colors.success;
  if (p === 'high_upside') return colors.dataPurple;
  if (p === 'category_punt') return colors.warning;
  if (p === 'dynasty_play') return colors.info;
  return colors.neutral[400];
};

const profileLabel = (p) => {
  if (p === 'safe_bet') return 'SAFE BET';
  if (p === 'high_upside') return 'HIGH UPSIDE';
  if (p === 'category_punt') return 'CATEGORY PUNT';
  if (p === 'dynasty_play') return 'DYNASTY PLAY';
  return (p || '').toUpperCase();
};

// T3-21: acceptColor single-sourced in optimizerUtils (consistent across Recs/Detail/Flyout)

const SectionLabel = ({ label }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: colors.primary, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8, marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 3, height: 12, background: colors.primary, borderRadius: 2 }} />
    {label}
  </div>
);

const CatPill = ({ cat, color: c }) => (
  <span style={{
    display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: 8, fontWeight: 700,
    background: `${c || colors.success}18`, color: c || colors.success,
    textTransform: 'uppercase', letterSpacing: 0.5,
  }}>{cat}</span>
);

const Badge = ({ label, color: c }) => (
  <span style={{
    padding: '2px 7px', borderRadius: 4, fontSize: 9, fontWeight: 700,
    background: `${c}18`, color: c, textTransform: 'uppercase', letterSpacing: 0.5,
  }}>{label}</span>
);

/* ── Metric Card ── */
const MetricCard = ({ label, value, sub, color: c }) => (
  <div style={{
    flex: '1 1 0', minWidth: 80, background: colors.neutral[850], borderRadius: 5,
    padding: '8px 10px', textAlign: 'center',
  }}>
    <div style={{ fontSize: 8, fontWeight: 700, color: colors.neutral[500], textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: c || colors.neutral[100] }}>{value}</div>
    {sub && <div style={{ fontSize: 8, color: colors.neutral[600], marginTop: 2 }}>{sub}</div>}
  </div>
);

/* ── Player Card ── */
const PlayerCard = ({ p, leagueId }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', marginBottom: 3,
    background: colors.neutral[800], borderRadius: 4,
  }}>
    <PlayerLink mlbId={p.player_id} name={p.player_name} leagueId={leagueId} style={{ fontSize: 13, fontWeight: 700, color: colors.neutral[100] }} />
    <span style={{ fontSize: 9, color: colors.neutral[500] }}>{positionLabel(p)}</span>
    {p.contract_salary != null && (
      <span style={{ fontSize: 9, color: colors.neutral[500] }}>${p.contract_salary}M · {p.contract_years || 1}yr</span>
    )}
    <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: colors.neutral[400] }}>
      {p.total_value?.toFixed(1) || '—'}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const OptimizerTradeDetail = ({ sequence, teams, onClose }) => {
  const navigate = useNavigate();
  const { leagueId } = useParams();

  if (!sequence) return null;

  const trade = sequence.trade_package || {};
  const sensitivity = sequence.sensitivity || {};
  const followUps = sequence.follow_up_moves || [];
  const validation = trade.validation || {};
  const sends = trade.user_sends || {};
  const receives = trade.user_receives || {};
  const sendPlayers = sends.players || [];
  const recvPlayers = receives.players || [];
  const sendPicks = sends.picks || [];
  const recvPicks = receives.picks || [];
  const fillsCats = trade.fills_needs?.categories || [];
  const fillsPos = trade.fills_needs?.positions || [];
  const sweeteners = sensitivity.sweetener_suggestions || [];
  const warnings = validation.warnings || [];

  const teamName = (tid) => {
    const team = teams?.find(t => t.team_id === tid);
    return team?.team_name || 'Unknown';
  };

  const oppTeamId = (trade.teams_involved || [])[1] || '';
  const accept = trade.acceptance_probability || 0;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 600, maxHeight: '85vh', overflowY: 'auto',
          background: colors.neutral[900], borderRadius: 10,
          border: `1px solid ${colors.neutral[700]}`, padding: '20px 24px',
        }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.neutral[100] }}>
              Trade with {teamName(oppTeamId)}
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
              {sensitivity.risk_profile && (
                <Badge label={profileLabel(sensitivity.risk_profile)} color={profileColor(sensitivity.risk_profile)} />
              )}
              {trade.variant && trade.variant !== 'fair' && (
                <Badge label={trade.variant.toUpperCase()} color={colors.info} />
              )}
              {sensitivity.is_on_frontier && (
                <Badge label="FRONTIER" color={colors.primary} />
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, borderRadius: 6, border: `1px solid ${colors.neutral[700]}`,
              background: 'transparent', color: colors.neutral[400], fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* ── Metric Cards — DD-RO-SEQBUGS-FIX B1: the MARGINAL net_value is the headline.
             Total = Trade(net_value, repair-marginal) + Follow-up, both swaps-against-
             replacement, summing to the total. trade_standings_gain (the whole-standings sim)
             is no longer surfaced here — it's a diagnostic, not the user's number. ── */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <MetricCard
            label="Total Gain"
            value={`${(sequence.total_standings_gain ?? 0) >= 0 ? '+' : ''}${(sequence.total_standings_gain ?? 0).toFixed(1)}`}
            sub="pts"
            color={(sequence.total_standings_gain ?? 0) > 0 ? colors.success : colors.neutral[400]}
          />
          {/* DD-RO-TRADE-VALUATION-0729 Part 1: net_value is the backfill-inclusive NET MARGINAL STANDINGS
              gain, NOT asset accounting — the old "Asset value / dynasty+prospect+ΔV" label was a misnomer
              that hid the star-bleed. Relabel to what it is. */}
          <MetricCard
            label="Net Standings Pts"
            value={`${(trade.net_value || 0) >= 0 ? '+' : ''}${(trade.net_value || 0).toFixed(1)}`}
            sub="net marginal (backfill-incl)"
            color={colors.neutral[300]}
          />
          <MetricCard
            label="Follow-ups"
            value={`${sequence.followup_standings_gain >= 0 ? '+' : ''}${sequence.followup_standings_gain || 0}`}
            sub={`${followUps.length} move${followUps.length !== 1 ? 's' : ''}`}
          />
          <MetricCard
            label="Acceptance"
            value={`${Math.round(accept * 100)}%`}
            color={acceptColor(accept)}
          />
        </div>

        {/* ── You Send / You Receive ── */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: colors.error, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>You Send</div>
            {sendPlayers.map((p, i) => <PlayerCard key={i} p={p} leagueId={leagueId} />)}
            {sendPicks.map((pk, i) => (
              <div key={`spk-${i}`} style={{ padding: '4px 8px', fontSize: 10, color: colors.neutral[400], background: colors.neutral[800], borderRadius: 4, marginBottom: 3 }}>
                Rd {pk.round} pick ({pk.year}) · val {pk.pick_value?.toFixed(1) || '—'}
              </div>
            ))}
            {sends.cash > 0 && <div style={{ fontSize: 10, color: colors.neutral[400], padding: '4px 8px' }}>${sends.cash}M cash</div>}
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.error, marginTop: 6, textAlign: 'right' }}>
              Total: {sends.total_value?.toFixed(1) || '—'}
            </div>
          </div>

          <div style={{ width: 1, background: colors.neutral[700] }} />

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: colors.success, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>You Receive</div>
            {recvPlayers.map((p, i) => <PlayerCard key={i} p={p} leagueId={leagueId} />)}
            {recvPicks.map((pk, i) => (
              <div key={`rpk-${i}`} style={{ padding: '4px 8px', fontSize: 10, color: colors.neutral[400], background: colors.neutral[800], borderRadius: 4, marginBottom: 3 }}>
                Rd {pk.round} pick ({pk.year}) · val {pk.pick_value?.toFixed(1) || '—'}
              </div>
            ))}
            {receives.cash > 0 && <div style={{ fontSize: 10, color: colors.neutral[400], padding: '4px 8px' }}>${receives.cash}M cash</div>}
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.success, marginTop: 6, textAlign: 'right' }}>
              Total: {receives.total_value?.toFixed(1) || '—'}
            </div>
          </div>
        </div>

        {/* ── Categories Addressed ── */}
        {(fillsCats.length > 0 || fillsPos.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, fontWeight: 600, color: colors.neutral[500] }}>Fills:</span>
            {fillsCats.map(c => <CatPill key={c} cat={c} />)}
            {fillsPos.map(p => <CatPill key={p} cat={p} color={colors.info} />)}
          </div>
        )}

        {/* ── Scenario Analysis ── */}
        {(sensitivity.upside_gain != null || sensitivity.downside_gain != null) && (
          <>
            <SectionLabel label="Scenario Analysis" />
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 5, background: `${colors.success}0c`, textAlign: 'center' }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: colors.success, textTransform: 'uppercase', letterSpacing: 0.8 }}>Upside</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.success }}>
                  {sensitivity.upside_gain >= 0 ? '+' : ''}{sensitivity.upside_gain?.toFixed(1) || '—'}
                </div>
                <div style={{ fontSize: 8, color: colors.neutral[500] }}>if projections beat +10%</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 5, background: colors.neutral[850], textAlign: 'center' }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: colors.neutral[400], textTransform: 'uppercase', letterSpacing: 0.8 }}>Expected</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.neutral[100] }}>
                  {/* DD-RO-SEQBUGS-FIX B3: expected = the band CENTER (net_value + follow-up),
                      the same basis upside/downside are measured from → downside ≤ expected ≤
                      upside. Was total_standings_gain (a different quantity), which read below
                      the downside. */}
                  {(sensitivity.expected_gain ?? 0) >= 0 ? '+' : ''}{(sensitivity.expected_gain ?? ((trade.net_value || 0) + (sequence.followup_standings_gain || 0))).toFixed(1)}
                </div>
                <div style={{ fontSize: 8, color: colors.neutral[500] }}>net + follow-up</div>
              </div>
              <div style={{
                flex: 1, padding: '8px 10px', borderRadius: 5, textAlign: 'center',
                background: (sensitivity.downside_gain || 0) >= 0 ? `${colors.success}0c` : `${colors.error}0c`,
              }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: (sensitivity.downside_gain || 0) >= 0 ? colors.success : colors.error, textTransform: 'uppercase', letterSpacing: 0.8 }}>Downside</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: (sensitivity.downside_gain || 0) >= 0 ? colors.success : colors.error }}>
                  {sensitivity.downside_gain >= 0 ? '+' : ''}{sensitivity.downside_gain?.toFixed(1) || '—'}
                </div>
                <div style={{ fontSize: 8, color: colors.neutral[500] }}>if projections miss -10%</div>
              </div>
            </div>
            {sensitivity.risk_explanation && (
              <div style={{ fontSize: 11, color: colors.neutral[400], fontStyle: 'italic', marginBottom: 8 }}>
                {sensitivity.risk_explanation}
              </div>
            )}
          </>
        )}

        {/* ── Per-Swap Effect ── DD-RO-ITEM3-EXECUTE-0729: SEPARATED per-swap sections from the enriched
             swap_chain, replacing the aggregate + the optional add-ons menu. Each swap (a sent player ↔ the
             received player OR the ONE committed backfill that fills his slot) is its OWN section, header
             "[out] → [in]", with the per-category effect (projected stat swing + rank move) across ONLY that
             swap's relevant cats (pitching for P↔P, hitting for a hitter swap). A backfill ties to the slot
             it fills — no cross-position "Buxton → pitcher". */}
        {(() => {
          const chain = (trade.swap_chain || []).filter(s => s.out || s.in);
          if (!chain.length) return null;
          const swapLabel = (s) => {
            const o = s.out?.player_name, i = s.in?.player_name;
            if (o && i) return `${o} → ${i}`;
            if (o && s.kind === 'drop') return `${o} → dropped`;
            if (o) return `${o} → (slot covered)`;
            if (i) return `＋ ${i}${s.kind === 'backfill' ? ' (backfill)' : ''}`;
            return '—';
          };
          const fmt = (sd) => (Math.abs(sd) < 1
            ? sd.toFixed(3).replace(/^(-?)0\./, '$1.')       // ratio stat keeps decimals
            : (sd > 0 ? '+' : '') + Math.round(sd));         // counting stat is whole
          // DD-RO-MOBILECATEGORYEFFECT-CRASH-0729: the enriched category_impact is a LIST of rows; a
          // pre-enrichment backend (not yet deployed) carries the old {category: points} DICT. Normalize both
          // so a deploy-ordering skew renders instead of `.map`-throwing.
          const impactRows = (ci) => (Array.isArray(ci) ? ci
            : (ci && typeof ci === 'object')
              ? Object.entries(ci).map(([category, pts]) => ({ category, pts_delta: pts }))
              : []);
          return (
            <>
              <SectionLabel label="Per-Swap Effect" />
              {chain.map((s, i) => (
                <div key={i} data-testid="swap-section" style={{ padding: '6px 10px', marginBottom: 4, borderRadius: 5, background: colors.neutral[850] }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.neutral[100], marginBottom: 4 }}>{swapLabel(s)}</div>
                  {/* DD-RO-TRADE-DESKTOP-TABLE-0729: render the per-swap effect as the SAME aligned TABLE the
                      mobile sheet uses (was a cramped flex-wrap of inline pills). Columns:
                      Category | Out (player+value) | In (player+value) | Net | Rank (before→after). */}
                  {(() => {
                    const rows = impactRows(s.category_impact);
                    if (!rows.length) return <span style={{ fontSize: 9, color: colors.neutral[600] }}>no category move</span>;
                    const th = { textAlign: 'right', padding: '2px 6px', fontWeight: 600, color: colors.neutral[500], fontVariantNumeric: 'tabular-nums' };
                    const thL = { ...th, textAlign: 'left' };
                    const td = { textAlign: 'right', padding: '2px 6px', color: colors.neutral[300], fontVariantNumeric: 'tabular-nums' };
                    const tdL = { ...td, textAlign: 'left', color: colors.neutral[400] };
                    const short = (n) => (n ? String(n).split(' ').slice(-1)[0] : '');
                    return (
                      <div style={{ overflowX: 'auto' }}>{/* DD-STAT-CONSOLIDATION C4: h-scroll for the per-swap table in the fixed-width modal */}
                      <table data-testid="swap-effect-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${colors.neutral[800]}` }}>
                            <th style={thL}>Cat</th>
                            <th style={th}>{short(s.out?.player_name) || 'Out'}</th>
                            <th style={th}>{short(s.in?.player_name) || 'In'}</th>
                            <th style={th}>Net</th>
                            <th style={th}>Rank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((ci) => (
                            <tr key={ci.category} data-testid="swap-effect-row" style={{ borderBottom: `1px solid ${colors.neutral[850]}` }}>
                              <td style={tdL}>{ci.category}</td>
                              <td style={td}>{s.out_proj?.[ci.category] ?? '—'}</td>
                              <td style={td}>{s.in_proj?.[ci.category] ?? '—'}</td>
                              <td style={{ ...td, fontWeight: 700, color: ci.stat_delta > 0 ? colors.success : ci.stat_delta < 0 ? colors.error : colors.neutral[400] }}>
                                {ci.stat_delta != null ? fmt(ci.stat_delta) : '—'}
                              </td>
                              <td style={td}>
                                {ci.rank_before != null && ci.rank_after != null
                                  ? (ci.rank_before !== ci.rank_after
                                    ? <span style={{ color: ci.rank_after < ci.rank_before ? colors.success : colors.neutral[400] }}>{ci.rank_before}→{ci.rank_after}</span>
                                    : <span style={{ color: colors.neutral[600] }}>{ci.rank_after}</span>)
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </>
          );
        })()}

        {/* ── Sweetener Suggestions ── */}
        {sweeteners.length > 0 && (
          <>
            <SectionLabel label="Sweetener Suggestions" />
            <div style={{ fontSize: 10, color: colors.neutral[400], marginBottom: 8 }}>
              Current acceptance: {Math.round(accept * 100)}%. Add assets to make the deal more appealing:
            </div>
            {sweeteners.map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                background: colors.neutral[850], borderRadius: 4, marginBottom: 3,
              }}>
                <Badge
                  label={s.asset_type === 'pick' ? 'PICK' : s.asset_type === 'cash' ? 'CASH' : 'PLAYER'}
                  color={s.asset_type === 'pick' ? colors.info : s.asset_type === 'cash' ? colors.primary : colors.dataPurple}
                />
                <span style={{ flex: 1, fontSize: 11, color: colors.neutral[200] }}>{s.add_asset}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: colors.success }}>→ {Math.round((s.new_acceptance || 0) * 100)}%</span>
                  <div style={{ fontSize: 8, color: colors.neutral[500] }}>
                    +{Math.round((s.acceptance_boost || 0) * 100)}% · costs {s.cost_to_you?.toFixed(1) || '—'}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── Validation Warnings ── */}
        {warnings.length > 0 && (
          <>
            <SectionLabel label="Warnings" />
            {warnings.map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '3px 0', fontSize: 11, color: colors.warning }}>
                <span>⚠</span>
                <span>{w}</span>
              </div>
            ))}
          </>
        )}

        {/* ── Footer ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${colors.neutral[700]}` }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px', fontSize: 11, fontWeight: 600,
              border: `1px solid ${colors.neutral[600]}`, borderRadius: 5,
              background: 'transparent', color: colors.neutral[300], cursor: 'pointer',
            }}
          >Close</button>
          <button
            onClick={() => {
              const mapPlayer = (p) => ({
                ids: { mlb: p.player_id },
                info: { full_name: p.player_name, position: p.position },
                league_context: { financial: { contract_salary: p.contract_salary, contract_years: p.contract_years } },
              });
              const mapPick = (p) => ({ pick_id: p.pick_id, round: p.round, year: p.year });
              const initialTradeState = {
                selectedTeamId: oppTeamId,
                iGive: {
                  players: (sends.players || []).map(mapPlayer),
                  picks: (sends.picks || []).map(mapPick),
                  cash: sends.cash || 0,
                },
                iGet: {
                  players: (receives.players || []).map(mapPlayer),
                  picks: (receives.picks || []).map(mapPick),
                  cash: receives.cash || 0,
                },
                notes: 'Suggested by Roster Optimizer',
              };
              onClose();
              navigate(`/leagues/${leagueId}`, { state: { activeSection: 'trade-center', initialTradeState } });
            }}
            style={{
              padding: '6px 16px', fontSize: 11, fontWeight: 600,
              border: `1px solid ${colors.primary}`, borderRadius: 5,
              background: `${colors.primary}15`, color: colors.primary, cursor: 'pointer',
            }}
          >Go to Trade Center</button>
        </div>
      </div>
    </div>
  );
};

export default OptimizerTradeDetail;
